import CachedIcon from "@mui/icons-material/Cached";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterNoneIcon from "@mui/icons-material/FilterNone";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, CardContent, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CandidateMovieRoot from "../Elements/CandidateMovieRoot";
import CandidateSeriesRoot from "../Elements/CandidateSeriesRoot";
import LoadingBackdrop from "../Elements/LoadingBackdrop";
import MovieCandidate from "../Elements/MovieCandidate";
import SeriesCandidate from "../Elements/SeriesCandidate";
import { getArchiveActionType } from "../utils/archiverUtils";
import {
  get,
  getAllShowsWithoutChildren,
  getConfiguration,
  post,
  waitForTaskToFinish,
} from "../utils/BackendClientLegacy";
import { validateCandidateRoot } from "../utils/candidateValidation";
import { GetDefaultSelectOption } from "../utils/DefaultObjects";
import { StyledCard } from "../utils/StyledComponents";

const limits = {
  seasonNumber: { min: 0, max: 99 },
  episodeNumber: { min: 0, max: 999 },
  multiEpisodeCount: { min: 1, max: 10 },
};

const defaultValues = { multiEpisodeCount: 1 };

const emptyCandidateRoot = {
  isEditState: false,
  isExpanded: false,
  archiveActionType: 0,
  archiveCandidateType: 0,
  path: "path",
  seriesId: 0,
  seasonNumber: 0,
  hasEnded: false,
  mediaRootPath: "",
  archiveCandidates: [
    {
      isEditState: false,
      archiveActionType: 0,
      archiveCandidateType: 0,
      hasEnded: false,
      path: "path",
      seriesId: 0,
      seasonNumber: 0,
      episodeNumber: 0,
      name: "name",
      episodeAlreadyExists: false,
      multiEpisodeCount: 1,
      additionalEpisodes: [{ episodeNumber: 0, name: "name", episodeAlreadyExists: false }],
    },
  ],
};

export const Archiver = () => {
  const [loading, setLoading] = useState(true);
  const [showHiddenFiles, setShowHiddenFiles] = useState(false);
  const [candidateRoots, setCandidateRoots] = useState([emptyCandidateRoot]);
  const [alert, setAlert] = useState({ level: "", message: "" });

  // Config/state loaded from backend
  const [actionTypes, setActionTypes] = useState([GetDefaultSelectOption()]);
  const [candidateTypes, setCandidateTypes] = useState([GetDefaultSelectOption()]);
  const [invalidCharacters, setInvalidCharacters] = useState([]);
  const [shows, setShows] = useState([
    { id: 0, externalId: 0, name: "", episodeNameRequired: false },
  ]);
  const noneType = useMemo(
    () => actionTypes.find((t) => t.label === "None")?.value ?? 0,
    [actionTypes],
  );

  const isSeriesType = useCallback(
    (archiveCandidateType) =>
      candidateTypes.find((t) => t.label === "Series")?.value === archiveCandidateType,
    [candidateTypes],
  );

  const validateRoot = useCallback(
    (root, isInitialLoad = false) =>
      validateCandidateRoot(
        root,
        candidateTypes,
        actionTypes,
        invalidCharacters,
        shows,
        isInitialLoad,
      ),
    [candidateTypes, actionTypes, invalidCharacters, shows],
  );

  const loadRoots = useCallback(
    async (loadHidden) => {
      const response = await get(`archive-candidates/roots?loadHidden=${loadHidden}`);
      const roots = await response.json();
      return roots.map((r) => validateRoot(r, true));
    },
    [validateRoot],
  );

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      const [loadedActionTypes, loadedCandidateTypes, loadedInvalidChars, loadedShows] =
        await Promise.all([
          getConfiguration("archive-action-types"),
          getConfiguration("archive-candidate-types"),
          getConfiguration("invalid-characters"),
          getAllShowsWithoutChildren(),
        ]);

      setActionTypes(loadedActionTypes);
      setCandidateTypes(loadedCandidateTypes);
      setInvalidCharacters(loadedInvalidChars);
      setShows(loadedShows);

      // Now that state is set, fetch candidates using direct variables to avoid stale closure
      const response = await get(`archive-candidates/roots?loadHidden=false`);
      const roots = await response.json();
      const validated = roots.map((r) =>
        validateCandidateRoot(
          r,
          loadedCandidateTypes,
          loadedActionTypes,
          loadedInvalidChars,
          loadedShows,
          true,
        ),
      );

      setCandidateRoots(validated);
      setLoading(false);
    };

    loadInitialData().catch((e) => {
      console.error(e);
      setAlert({ level: "error", message: "Failed to load archiver data." });
      setLoading(false);
    });
  }, []);

  const multiEpisodeSignature = useMemo(() => {
    // create a stable “signature” from the values you care about
    return candidateRoots
      .flatMap((r) => (r.archiveCandidates ?? []).map((c) => c.multiEpisodeCount ?? ""))
      .join("|");
  }, [candidateRoots]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCandidateRoots((prev) => {
        let changed = false;

        const next = prev.map((root) => {
          const nextCandidates = (root.archiveCandidates ?? []).map((c) => {
            if (c.multiEpisodeCount === "") {
              changed = true;
              return { ...c, multiEpisodeCount: defaultValues.multiEpisodeCount };
            }
            return c;
          });

          return changed ? { ...root, archiveCandidates: nextCandidates } : root;
        });

        return changed ? next : prev;
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [multiEpisodeSignature, setCandidateRoots]);

  /* ------------------------------ read helpers ------------------------------ */

  const getRootByPath = useCallback(
    (path) => candidateRoots.find((r) => r.path === path),
    [candidateRoots],
  );

  const getCandidateByPath = useCallback(
    (path) => {
      for (const r of candidateRoots) {
        const found = r.archiveCandidates?.find((c) => c.path === path);
        if (found) return found;
      }
      return undefined;
    },
    [candidateRoots],
  );

  /* ------------------------------ update helpers ---------------------------- */

  const updateRoot = useCallback(
    (path, updater) => {
      setCandidateRoots((prev) => {
        const idx = prev.findIndex((r) => r.path === path);
        if (idx === -1) return prev;

        const original = prev[idx];
        const updated = updater(structuredCloneSafe(original));
        const validated = validateRoot(updated, false);

        const next = [...prev];
        next[idx] = validated;
        return next;
      });
    },
    [validateRoot],
  );

  const updateCandidate = useCallback(
    (candidatePath, updater) => {
      setCandidateRoots((prev) => {
        const rootIdx = prev.findIndex((r) =>
          r.archiveCandidates?.some((c) => c.path === candidatePath),
        );
        if (rootIdx === -1) return prev;

        const root = prev[rootIdx];
        const candidateIdx = root.archiveCandidates.findIndex((c) => c.path === candidatePath);
        if (candidateIdx === -1) return prev;

        const rootCopy = structuredCloneSafe(root);
        const candidateCopy = structuredCloneSafe(rootCopy.archiveCandidates[candidateIdx]);

        rootCopy.archiveCandidates[candidateIdx] = updater(candidateCopy);

        const validatedRoot = validateRoot(rootCopy, false);

        const next = [...prev];
        next[rootIdx] = validatedRoot;
        return next;
      });
    },
    [validateRoot],
  );

  /* -------------------------------- actions -------------------------------- */

  const loadHiddenFiles = useCallback(async () => {
    setLoading(true);
    const roots = await loadRoots(true);
    setCandidateRoots(roots);
    setShowHiddenFiles(true);
    setLoading(false);
  }, [loadRoots]);

  const deactivateAllRoots = useCallback(() => {
    setCandidateRoots((prev) =>
      prev.map((r) =>
        validateRoot({ ...structuredCloneSafe(r), archiveActionType: noneType }, false),
      ),
    );
  }, [noneType, validateRoot]);

  const onCandidateRootTypeOptionChange = useCallback(
    (event) => {
      const path = event.currentTarget.name;
      const currentValue = parseInt(event.currentTarget.id, 10);

      updateRoot(path, (root) => {
        // Toggle between types: pick first that is NOT currentValue
        root.archiveCandidateType =
          candidateTypes.find((t) => t.value !== currentValue)?.value ?? root.archiveCandidateType;
        return root;
      });
    },
    [candidateTypes, updateRoot],
  );

  const onRootActionIconClick = useCallback(
    (event) => {
      const path = event.currentTarget.name;
      const clicked = getArchiveActionType(event.currentTarget.id, actionTypes)?.value;

      updateRoot(path, (root) => {
        if (clicked === undefined) return root;

        if (clicked === root.archiveActionType) {
          root.archiveActionType = root.archiveCandidates.length === 1 ? 0 : null;
        } else {
          root.archiveActionType = clicked;
        }
        return root;
      });
    },
    [actionTypes, updateRoot],
  );

  const onRootSelectOptionChange = useCallback(
    (event) => {
      const path = event.target.name;
      const key = event.target.id;
      const value = parseInt(event.target.value, 10);

      updateRoot(path, (root) => {
        root[key] = value === 0 ? null : value;
        return root;
      });
    },
    [updateRoot],
  );

  const onExpandCard = useCallback(
    (event) => {
      const path = event.currentTarget.name;
      updateRoot(path, (root) => {
        root.isExpanded = !(root.isExpanded ?? false);
        return root;
      });
    },
    [updateRoot],
  );

  const handleRootEditClick = useCallback(
    (event) => {
      const path = event.currentTarget.name;
      updateRoot(path, (root) => {
        root.isEditState = !(root.isEditState ?? false);
        return root;
      });
    },
    [updateRoot],
  );

  const handleEditClick = useCallback(
    (event) => {
      const candidatePath = event.currentTarget.name;
      updateCandidate(candidatePath, (candidate) => {
        candidate.isEditState = !(candidate.isEditState ?? false);
        return candidate;
      });
    },
    [updateCandidate],
  );

  const onSelectOptionChange = useCallback(
    (event) => {
      const candidatePath = event.target.name;
      const key = event.target.id;
      const value = parseInt(event.target.value, 10);

      updateCandidate(candidatePath, (candidate) => {
        candidate[key] = value === 0 ? null : value;
        return candidate;
      });
    },
    [updateCandidate],
  );

  const onActionIconClick = useCallback(
    (event) => {
      const candidatePath = event.currentTarget.name;
      const clicked = getArchiveActionType(event.currentTarget.id, actionTypes)?.value;

      updateCandidate(candidatePath, (candidate) => {
        if (clicked === undefined) return candidate;
        candidate.archiveActionType = clicked === candidate.archiveActionType ? 0 : clicked;
        return candidate;
      });
    },
    [actionTypes, updateCandidate],
  );

  const onCandidateTextFieldChange = useCallback(
    (event) => {
      const candidatePath = event.target.name;
      const key = event.target.id;
      const raw = event.target.value;

      updateCandidate(candidatePath, (candidate) => {
        if (!isNaN(emptyCandidateRoot.archiveCandidates[0][key])) {
          const limit = limits[key];
          const parsed = parseInt(raw, 10);

          if (Number.isFinite(parsed) && (parsed < limit?.min || parsed > limit?.max)) {
            return candidate;
          }

          candidate[key] = Number.isNaN(parsed) ? "" : parsed;
        } else {
          candidate[key] = raw;
        }
        return candidate;
      });
    },
    [updateCandidate],
  );

  const onCandidateRootTextFieldChange = useCallback(
    (event) => {
      const path = event.target.name;
      const key = event.target.id;
      const raw = event.target.value;

      updateRoot(path, (root) => {
        if (!isNaN(root[key])) {
          const limit = limits[key];
          const parsed = parseInt(raw, 10);

          if (Number.isFinite(parsed) && (parsed < limit?.min || parsed > limit?.max)) {
            return root;
          }

          root[key] = Number.isNaN(parsed) ? "" : parsed;
        } else {
          root[key] = raw;
        }
        return root;
      });
    },
    [updateRoot],
  );

  const onAdditionalTitleChange = useCallback(
    (event) => {
      const candidatePath = event.target.name;
      const index = parseInt(event.target.id, 10);
      const value = event.target.value;

      updateCandidate(candidatePath, (candidate) => {
        candidate.additionalEpisodes = candidate.additionalEpisodes ?? [];

        candidate.additionalEpisodes[index] = candidate.additionalEpisodes[index] ?? {
          episodeNumber: (candidate.episodeNumber ?? 0) + index + 1,
          episodeAlreadyExists: false,
        };

        candidate.additionalEpisodes[index].name = value;
        return candidate;
      });
    },
    [updateCandidate],
  );

  const onToggleHide = useCallback(
    async (event) => {
      const path = event.currentTarget.name;
      const root = getRootByPath(path);
      if (!root) return;

      onRootActionIconClick(event);
      await get(`archive-candidates/roots/${root.id}/toggle-hide`);
    },
    [getRootByPath, onRootActionIconClick],
  );

  const onCandidateUpdate = useCallback(
    async (event) => {
      const candidatePath = event.currentTarget.name;
      const candidate = getCandidateByPath(candidatePath);
      if (!candidate) return;

      const response = await post("archive-candidates/update-metadata", candidate);
      if (response.status === 200) {
        const updatedCandidate = await response.json();
        updatedCandidate.isEditState = true; // user initiated
        updateCandidate(candidatePath, () => updatedCandidate);
      }
    },
    [getCandidateByPath, updateCandidate],
  );

  const onCandidateRootUpdate = useCallback(
    async (event) => {
      const path = event.currentTarget.name;
      const root = getRootByPath(path);
      if (!root) return;

      const response = await post("archive-candidates/roots/update-metadata", root);
      if (response.status === 200) {
        const updatedRoot = await response.json();
        updatedRoot.isEditState = true; // user initiated
        updateRoot(path, () => updatedRoot);
      }
    },
    [getRootByPath, updateRoot],
  );

  const navigate = useNavigate();

  const onExecute = useCallback(async () => {
    setLoading(true);
    await post("archive-candidates/roots/execute", candidateRoots);

    const pollingEndpoint = "archive-candidates/archiving";
    await waitForTaskToFinish(pollingEndpoint, { loading: true, alert }, (s) => {
      if (typeof s === "function") return;
      setLoading(!!s.loading);
      setAlert(s.alert ?? alert);
    });

    // show live results while it runs
    navigate("/archiver-logs?mode=live");
  }, [candidateRoots, alert, navigate]);

  const onReindex = useCallback(async () => {
    setLoading(true);
    await post("archive-candidates/reindex", {});
    await waitForTaskToFinish("archive-candidates/indexing", { loading: true, alert }, (s) => {
      if (typeof s === "function") return;
      setLoading(!!s.loading);
      setAlert(s.alert ?? alert);
    });
    const roots = await loadRoots(false);
    setCandidateRoots(roots);
    setLoading(false);
  }, [alert, loadRoots]);

  /* -------------------------------- rendering ------------------------------ */

  const renderCandidate = useCallback(
    (candidate, candidateRoot, index) => {
      if (isSeriesType(candidateRoot.archiveCandidateType)) {
        return (
          <SeriesCandidate
            key={candidate.path}
            lightBackground={index % 2 !== 0}
            candidate={candidate}
            candidateRoot={candidateRoot}
            shows={shows}
            actionTypes={actionTypes}
            onActionIconClick={onActionIconClick}
            handleEditClick={handleEditClick}
            onCandidateTextFieldChange={onCandidateTextFieldChange}
            onCandidateUpdate={onCandidateUpdate}
            onAdditionalTitleChange={onAdditionalTitleChange}
            onSelectOptionChange={onSelectOptionChange}
          />
        );
      }

      return (
        <MovieCandidate
          key={candidate.path}
          lightBackground={index % 2 !== 0}
          candidate={candidate}
          candidateRoot={candidateRoot}
          actionTypes={actionTypes}
          onActionIconClick={onActionIconClick}
          onCandidateTextFieldChange={onCandidateTextFieldChange}
        />
      );
    },
    [
      actionTypes,
      handleEditClick,
      isSeriesType,
      onActionIconClick,
      onAdditionalTitleChange,
      onCandidateTextFieldChange,
      onCandidateUpdate,
      onSelectOptionChange,
      shows,
    ],
  );

  const renderCandidateRoot = useCallback(
    (candidateRoot) => {
      if (isSeriesType(candidateRoot.archiveCandidateType)) {
        return (
          <CandidateSeriesRoot
            candidateRoot={candidateRoot}
            shows={shows}
            onExpandCard={onExpandCard}
            onRootActionIconClick={onRootActionIconClick}
            onRootSelectOptionChange={onRootSelectOptionChange}
            candidateTypes={candidateTypes}
            actionTypes={actionTypes}
            onCandidateRootUpdate={onCandidateRootUpdate}
            handleRootEditClick={handleRootEditClick}
            onHide={onToggleHide}
            onCandidateRootTextFieldChange={onCandidateRootTextFieldChange}
            onCandidateRootTypeOptionChange={onCandidateRootTypeOptionChange}
          />
        );
      }

      return (
        <CandidateMovieRoot
          candidateRoot={candidateRoot}
          onExpandCard={onExpandCard}
          onRootActionIconClick={onRootActionIconClick}
          onHide={onToggleHide}
          candidateTypes={candidateTypes}
          actionTypes={actionTypes}
          onCandidateRootTypeOptionChange={onCandidateRootTypeOptionChange}
        />
      );
    },
    [
      actionTypes,
      candidateTypes,
      handleRootEditClick,
      isSeriesType,
      onCandidateRootTextFieldChange,
      onCandidateRootTypeOptionChange,
      onCandidateRootUpdate,
      onExpandCard,
      onRootActionIconClick,
      onRootSelectOptionChange,
      onToggleHide,
      shows,
    ],
  );

  const renderCards = useCallback(
    (candidateRoot) => (
      <StyledCard key={candidateRoot.path}>
        {renderCandidateRoot(candidateRoot)}
        <CardContent style={{ padding: 0, textAlign: "center" }}>
          <IconButton
            id="isExpanded"
            name={candidateRoot.path}
            onClick={onExpandCard}
            aria-label="expanded"
          >
            {candidateRoot.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </CardContent>

        {candidateRoot.isExpanded
          ? candidateRoot.archiveCandidates.map((c, index) =>
              renderCandidate(c, candidateRoot, index),
            )
          : null}
      </StyledCard>
    ),
    [onExpandCard, renderCandidate, renderCandidateRoot],
  );

  const renderContent = () => {
    if (loading) {
      return <LoadingBackdrop alert={alert} />;
    }

    return (
      <>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              fullWidth
              onClick={loadHiddenFiles}
              disabled={showHiddenFiles}
            >
              Show hidden files
            </Button>
          </Grid>

          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterNoneIcon />}
              fullWidth
              onClick={deactivateAllRoots}
            >
              Deactivate all
            </Button>
          </Grid>

          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CachedIcon />}
              fullWidth
              onClick={onReindex}
            >
              Reindex
            </Button>
          </Grid>

          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayCircleOutlineIcon />}
              fullWidth
              onClick={onExecute}
            >
              Execute
            </Button>
          </Grid>
        </Grid>

        {candidateRoots.map(renderCards)}
      </>
    );
  };

  return renderContent();
};

/* -------------------------------- utilities ------------------------------- */

// Avoid crashing older runtimes. In modern browsers/node, structuredClone exists.
function structuredCloneSafe(obj) {
  if (typeof structuredClone === "function") return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
}
